import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowUp } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { insertProfileVideoAction } from "../../../../store/ProfileVideoActions";
import { useAxiosInstance } from "../../../../services/Interceptor";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useState } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import PremiumFeatureModal from "../../../../components/PremiumFeatureModal";
import FreeTrialModal from "../../../../components/FreeTrialModal";
import { StartFreeTrial } from "../../../../services/PaymentService";
import { usePremiumUpsellRedirect } from "../../../../hooks/usePremiumUpsellRedirect";
import { setProfileAction } from "../../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../../store/userActions";
import { bumpMenuRefresh } from "../../../../store/slices/athleteSlice";

export default function UploadVideo({ profileVideo, savedVideos }) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const resultFilter = savedVideos.filter(
    (item) => item.type === profileVideo.id,
  );
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const authUser = useAuthUser();
  const { user = {} } = authUser || {};
  const hasSubscription = useSelector((state) => state.user.hasSubscription);
  const hasUsedFreeTrial = useSelector((state) => state.user.hasUsedFreeTrial);
  const hasSubscriptionAccess =
    hasSubscription || user?.role?.toLowerCase?.() === "highschool";
  const showFreeTrial =
    !hasSubscriptionAccess && user?.role === "athlete" && !hasUsedFreeTrial;

  const openSubscription = usePremiumUpsellRedirect(user);

  const [openTrialModal, setOpenTrialModal] = useState(false);

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handleLabelClick = (e) => {
    if (!hasSubscriptionAccess) {
      e.preventDefault();
      setOpenPremiumFeatureModal(true);
    }
  };

  const handleSubmitProfileVideo = async (event, profileVideoId) => {
    const file = event.target.files[0];
    if (!file) {
      toast.warn("No file selected. Please choose a video to upload.");
      return;
    }

    if (!file.type.startsWith("video/")) {
      toast.error("Invalid file type. Please upload a valid video file.", {});
      return;
    }

    const maxSizeInBytes = 600 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      toast.warning("Video size should not exceed 600MB.", {
        position: "top-right",
      });
      return;
    }

    try {
      await dispatch(
        insertProfileVideoAction(
          apiInstance,
          file,
          profileVideoId,
          setUploadProgress,
        ),
      );
      toast.success("Video uploaded successfully!");
    } catch (error) {
      toast.error(
        "An error occurred while uploading the video. Please try again.",
      );
    }
  };

  return (
    <>
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Video uploads are locked. Start a free trial or subscribe now to upload your videos."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={() => {
          setOpenPremiumFeatureModal(false);
          openSubscription();
        }}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      {resultFilter.length === 0 && (
        <div className="w-50">
          <div className="w-full flex flex-col items-center">
            <div className="w-full border border-blue-700 border-dashed rounded-lg cursor-pointer p-2">
              <label
                htmlFor={`video-${profileVideo.id}`}
                className="w-full flex flex-col items-center cursor-pointer"
                onClick={handleLabelClick}
              >
                <FontAwesomeIcon
                  icon={faCircleArrowUp}
                  size="lg"
                  className="text-gray-500"
                />
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-normal">Click to upload Video</span>
                </p>
                <input
                  id={`video-${profileVideo.id}`}
                  type="file"
                  className="hidden"
                  onChange={(e) => handleSubmitProfileVideo(e, profileVideo.id)}
                  accept="video/*"
                />
              </label>
            </div>
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="w-full mt-3">
                <div className="bg-gray-300 rounded-full h-2 w-full">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-600 mt-1 text-right pr-1">
                  Uploading: {uploadProgress}%
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
